import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  dur,
  dt,
  todaySplitOf,
  fuelToday,
  info,
  STATUS_HEX,
  ic,
} from './trackingUtils';

const VehicleTrackingSummaryStrip = ({
  mode = 'live',
  vehicles = [],
  period = 'today',
  customRange = null,
  fleetHistoryData = null,
  activeFilterKey = '',
  onFilterClick,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const { totalViolationsToday = 0, globalViolations = [] } = useSelector(
    (state) => state.vehicleTracking || {}
  );

  const hshort = (m) => {
    m = Math.round(m || 0);
    return m >= 600 ? `${Math.round(m / 60)}h` : m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${m % 60}m`;
  };

  if (mode === 'history') {
    const D =
      fleetHistoryData && fleetHistoryData.length > 0
        ? fleetHistoryData
        : vehicles.map((v) => ({
            v,
            km: v.km || 0,
            mv: v.st === 'moving' ? 1 : 0,
            idle: v.st === 'idle' ? 1 : 0,
            stp: v.st === 'stopped' ? 1 : 0,
            stops: v.stops?.length || 0,
            trips: v.trip ? [v.trip] : [],
            fuel: v.km ? Math.round(v.km / 3.5) : 0,
            max: v.spd || 0,
          }));
    const T = D.reduce(
      (a, d) => ({
        km: a.km + d.km,
        mv: a.mv + d.mv,
        idle: a.idle + d.idle,
        stp: a.stp + d.stp,
        stops: a.stops + d.stops,
        fuel: a.fuel + d.fuel,
        max: Math.max(a.max, d.max),
      }),
      { km: 0, mv: 0, idle: 0, stp: 0, stops: 0, fuel: 0, max: 0 }
    );
    const moved = D.filter((d) => d.km > 1).length;
    const tot = Math.max(1, T.mv + T.idle + T.stp);
    const w = (x) => ((x / tot) * 100).toFixed(1);

    const allTrips = D.flatMap((d) => d.trips);
    const doneTrips = allTrips.filter((t) => t.st === 'done').length;
    const liveTrips = allTrips.length - doneTrips;
    const lateVehicles = D.filter((d) => d.trips.some((t) => t.late >= 10)).length;
    const offD = D.filter((d) => d.offKm >= 1);
    const offKm = offD.reduce((a, d) => a + d.offKm, 0);

    const periodTxt =
      period === 'today'
        ? 'Today'
        : period === 'yesterday'
        ? 'Yesterday'
        : period === '24h'
        ? 'Last 24h'
        : 'Custom Period';

    return (
      <div
        className={`hf-strip lt-strip ${collapsed ? 'collapsed' : ''}`}
        aria-label="Fleet history summary"
      >
        {/* Mini view when collapsed */}
        <div className="fs-mini" aria-hidden={!collapsed}>
          <span className="fm-k">Fleet · {periodTxt}</span>
          <span>
            <b>{D.length}</b> vehicles
          </span>
          <span>
            <b className="num">{Math.round(T.km).toLocaleString('en-US')}</b> km
          </span>
          <span className="fm-bar" title={`Driving ${dur(T.mv)} · Idle ${dur(T.idle)} · Stopped ${dur(T.stp)}`}>
            <span style={{ width: `${w(T.mv)}%`, background: STATUS_HEX.moving }} />
            <span style={{ width: `${w(T.idle)}%`, background: STATUS_HEX.idle }} />
            <span style={{ width: `${w(T.stp)}%`, background: STATUS_HEX.stopped }} />
          </span>
          <span>
            <b className="num">{doneTrips}</b> trips done
            {liveTrips ? ` · ${liveTrips} in progress` : ''}
          </span>
          {lateVehicles > 0 && (
            <button
              type="button"
              className={`fs-a w ${activeFilterKey === 'late' ? 'on' : ''}`}
              onClick={() => onFilterClick && onFilterClick('late')}
            >
              {lateVehicles} late
            </button>
          )}
          {offKm >= 1 && (
            <button
              type="button"
              className={`fs-a pk ${activeFilterKey === 'off' ? 'on' : ''}`}
              onClick={() => onFilterClick && onFilterClick('off')}
            >
              {Math.round(offKm)} km off-trip
            </button>
          )}
        </div>

        {/* Expanded KPI Blocks */}
        <div className="fs-k">
          <small>Fleet · {periodTxt}</small>
          <b>{D.length} vehicles</b>
          <span className="muted">{moved} moved</span>
        </div>

        <div className="fs-k">
          <small>Distance</small>
          <b className="num">{Math.round(T.km).toLocaleString('en-US')} km</b>
        </div>

        <div className="fs-k fs-split">
          <small>Time · {dur(tot)} total</small>
          <span className="fs-bar">
            <span style={{ width: `${w(T.mv)}%`, background: STATUS_HEX.moving }} title={`Driving ${dur(T.mv)}`} />
            <span style={{ width: `${w(T.idle)}%`, background: STATUS_HEX.idle }} title={`Idle ${dur(T.idle)}`} />
            <span style={{ width: `${w(T.stp)}%`, background: STATUS_HEX.stopped }} title={`Stopped ${dur(T.stp)}`} />
          </span>
          <span className="fs-leg">
            <i style={{ background: STATUS_HEX.moving }} />
            Driving {hshort(T.mv)}
            <i style={{ background: STATUS_HEX.idle }} />
            Idle {hshort(T.idle)}
            <i style={{ background: STATUS_HEX.stopped }} />
            Stopped {hshort(T.stp)} ({T.stops})
          </span>
        </div>

        <div className="fs-k">
          <small>Est. fuel</small>
          <b className="num">{Math.round(T.fuel).toLocaleString('en-US')} L</b>
        </div>

        <div className="fs-k">
          <small>Trips in period</small>
          <b className="num">
            {doneTrips} completed{liveTrips ? ` · ${liveTrips} in progress` : ''}
          </b>
          <span className="fs-sub">
            {lateVehicles > 0 ? (
              <button
                type="button"
                className={`fs-a w ${activeFilterKey === 'late' ? 'on' : ''}`}
                onClick={() => onFilterClick && onFilterClick('late')}
              >
                {lateVehicles} started late
              </button>
            ) : (
              'none started late'
            )}
          </span>
        </div>

        <div className="fs-k">
          <small>Off-trip movement</small>
          <b className={`num ${offKm >= 1 ? 'pk' : ''}`}>
            {Math.round(offKm).toLocaleString('en-US')} km
          </b>
          <span className="fs-sub">
            {offD.length > 0 ? (
              <button
                type="button"
                className={`fs-a pk ${activeFilterKey === 'off' ? 'on' : ''}`}
                onClick={() => onFilterClick && onFilterClick('off')}
              >
                on {offD.length} vehicle{offD.length === 1 ? '' : 's'}
              </button>
            ) : (
              'none'
            )}
          </span>
        </div>

        {/* Collapse toggle */}
        <button
          type="button"
          className="fs-col"
          onClick={() => setCollapsed(!collapsed)}
          title={collapsed ? 'Show summary' : 'Collapse summary'}
          aria-label={collapsed ? 'Show summary' : 'Collapse summary'}
        >
          <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              transform: collapsed ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            <path d="m18 15-6-6-6 6" />
          </svg>
        </button>
      </div>
    );
  }

  // LIVE MODE
  const totals = vehicles.reduce(
    (acc, v) => {
      const i = info(v);
      const t = todaySplitOf(v, i);
      const f = fuelToday(v);
      return {
        km: acc.km + (v.km || 0),
        drive: acc.drive + (t.drive || 0),
        idle: acc.idle + (t.idle || 0),
        stop: acc.stop + (t.stop || 0),
        fuel: acc.fuel + (f.total || 0),
      };
    },
    { km: 0, drive: 0, idle: 0, stop: 0, fuel: 0 }
  );

  const avgKmL = totals.fuel > 0 ? (totals.km / totals.fuel).toFixed(1) : '—';

  let onTripCount = 0;
  let delayedCount = 0;
  let overdueCount = 0;
  let offTripCount = 0;
  let totalViolations = 0;
  let vioVehicles = 0;

  vehicles.forEach((v) => {
    const i = info(v);
    if (i.onTrip) onTripCount++;
    if (i.status !== 'moving' && i.onTrip && (Date.now() - (v.since || Date.now())) / 60000 >= 5) {
      delayedCount++;
    }
    if (i.tripStatus === '—' && i.status === 'moving' && !i.offline) {
      offTripCount++;
    }
    const vLen = v.vio ? v.vio.length : 0;
    totalViolations += vLen;
    if (vLen > 0) vioVehicles++;
  });

  // Factor in real-time fleet violations received via vehicle-violation-alert
  if (totalViolationsToday > totalViolations || globalViolations.length > totalViolations) {
    totalViolations = Math.max(totalViolations, totalViolationsToday, globalViolations.length);
    if (vioVehicles === 0 && totalViolations > 0) {
      vioVehicles = 1;
    }
  }

  const spanTotal = Math.max(1, totals.drive + totals.idle + totals.stop);
  const w = (x) => ((x / spanTotal) * 100).toFixed(1);
  const day = dt(Date.now()).slice(0, 6);

  return (
    <div className={`hf-strip lt-strip ${collapsed ? 'collapsed' : ''}`} aria-label="Today summary">
      {/* Mini Collapsed Bar */}
      <div className="fs-mini" aria-hidden={!collapsed}>
        <span className="fm-k">Today · {day}</span>
        <span>
          <b>{vehicles.length}</b> vehicles
        </span>
        <span>
          <b className="num">{Math.round(totals.km).toLocaleString('en-US')}</b> km
        </span>
        <span className="fm-bar" title="Driving, Idle, Stopped">
          <span style={{ width: `${w(totals.drive)}%`, background: STATUS_HEX.moving }} />
          <span style={{ width: `${w(totals.idle)}%`, background: STATUS_HEX.idle }} />
          <span style={{ width: `${w(totals.stop)}%`, background: STATUS_HEX.stopped }} />
        </span>
        <span>
          <b className="num">{onTripCount}</b> on trip
        </span>
        {delayedCount > 0 && (
          <button
            type="button"
            className={`fs-a w ${activeFilterKey === 'delayed' ? 'on' : ''}`}
            onClick={() => onFilterClick && onFilterClick('delayed')}
          >
            {delayedCount} delayed
          </button>
        )}
        {offTripCount > 0 && (
          <button
            type="button"
            className={`fs-a pk ${activeFilterKey === 'offtrip' ? 'on' : ''}`}
            onClick={() => onFilterClick && onFilterClick('offtrip')}
          >
            {offTripCount} off-trip
          </button>
        )}
        <span>
          {totalViolations > 0 ? (
            <button
              type="button"
              className={`fs-a r ${activeFilterKey === 'vio' ? 'on' : ''}`}
              onClick={() => onFilterClick && onFilterClick('vio')}
            >
              {totalViolations} violations
            </button>
          ) : (
            '0 violations'
          )}
        </span>
      </div>

      {/* Expanded KPI Blocks */}
      <div className="fs-k">
        <small>Today · {day}</small>
        <b>
          {vehicles.length} vehicle{vehicles.length === 1 ? '' : 's'}
        </b>
        <span className="muted">in view</span>
      </div>

      <div className="fs-k">
        <small>Distance today</small>
        <b className="num">{Math.round(totals.km).toLocaleString('en-US')} km</b>
      </div>

      <div className="fs-k fs-split">
        <small>Time today · {dur(spanTotal)} total</small>
        <span className="fs-bar">
          <span style={{ width: `${w(totals.drive)}%`, background: STATUS_HEX.moving }} title={`Driving ${dur(totals.drive)}`} />
          <span style={{ width: `${w(totals.idle)}%`, background: STATUS_HEX.idle }} title={`Idle ${dur(totals.idle)}`} />
          <span style={{ width: `${w(totals.stop)}%`, background: STATUS_HEX.stopped }} title={`Stopped ${dur(totals.stop)}`} />
        </span>
        <span className="fs-leg">
          <i style={{ background: STATUS_HEX.moving }} />
          Driving {hshort(totals.drive)}
          <i style={{ background: STATUS_HEX.idle }} />
          Idle {hshort(totals.idle)}
          <i style={{ background: STATUS_HEX.stopped }} />
          Stopped {hshort(totals.stop)}
        </span>
      </div>

      <div className="fs-k">
        <small>Est. fuel today</small>
        <b className="num">{Math.round(totals.fuel).toLocaleString('en-US')} L</b>
      </div>

      <div className="fs-k">
        <small>Fleet avg km/L</small>
        <b className="num">{avgKmL}</b>
      </div>

      <div className="fs-k">
        <small>Trips</small>
        <b className="num">{onTripCount} on trip</b>
        <span className="fs-sub">
          {delayedCount > 0 ? (
            <button
              type="button"
              className={`fs-a w ${activeFilterKey === 'delayed' ? 'on' : ''}`}
              onClick={() => onFilterClick && onFilterClick('delayed')}
            >
              {delayedCount} delayed
            </button>
          ) : (
            'none delayed'
          )}
          {overdueCount > 0 && (
            <>
              {' · '}
              <button
                type="button"
                className={`fs-a r ${activeFilterKey === 'overdue' ? 'on' : ''}`}
                onClick={() => onFilterClick && onFilterClick('overdue')}
              >
                {overdueCount} start overdue
              </button>
            </>
          )}
        </span>
      </div>

      {offTripCount > 0 && (
        <div className="fs-k">
          <small>Off-trip</small>
          <button
            type="button"
            className={`fs-a pk big ${activeFilterKey === 'offtrip' ? 'on' : ''}`}
            onClick={() => onFilterClick && onFilterClick('offtrip')}
          >
            {offTripCount}
          </button>
          <span className="fs-sub">moving, no trip</span>
        </div>
      )}

      <div className="fs-k">
        <small>Violations today</small>
        {totalViolations > 0 ? (
          <button
            type="button"
            className={`fs-a r big ${activeFilterKey === 'vio' ? 'on' : ''}`}
            onClick={() => onFilterClick && onFilterClick('vio')}
          >
            {totalViolations}
          </button>
        ) : (
          <b className="num">0</b>
        )}
        <span className="fs-sub">
          {totalViolations > 0
            ? `on ${vioVehicles} vehicle${vioVehicles === 1 ? '' : 's'}`
            : 'none'}
        </span>
      </div>

      {/* Collapse Button */}
      <button
        type="button"
        className="fs-col"
        onClick={() => setCollapsed(!collapsed)}
        title={collapsed ? 'Show summary' : 'Collapse summary'}
        aria-label="Toggle summary collapse"
      >
        <svg
          viewBox="0 0 24 24"
          width="14"
          height="14"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            transform: collapsed ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <path d="m18 15-6-6-6 6" />
        </svg>
      </button>
    </div>
  );
};

export default VehicleTrackingSummaryStrip;
